import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, X, Hash } from 'lucide-react';
import { WorkflowStep, WorkflowOutput, WorkflowInput, ExpandedSections, Workflow } from '../types/workflow';
import { CONSTANT_STEPS } from '../constants/stepTemplates';
import { cn } from '../utils/cn';
import { parseFunctionSignatureWithInputs } from '../utils/functionSignatureParser';
import { WorkflowExecutor } from '../services/workflowExecutor';

interface StepEditorProps {
  step: WorkflowStep;
  onUpdate: (updatedStep: WorkflowStep) => void;
  isConstant: boolean;
  workflow: Workflow;
}

const StepEditor: React.FC<StepEditorProps> = ({ step, onUpdate, isConstant, workflow }) => {
  const [expandedSections, setExpandedSections] = useState<ExpandedSections>({
    config: true,
    inputs: true,
    outputs: true
  });

  const [autoMapLoading, setAutoMapLoading] = useState<boolean>(false);
  const [autoMapError, setAutoMapError] = useState<string | null>(null);

  // Draft text for object-valued config fields to allow free typing before valid JSON
  const [configDrafts, setConfigDrafts] = useState<Record<string, string>>({});
  useEffect(() => {
    const nextDrafts: Record<string, string> = {};
    Object.entries(step.config || {}).forEach(([k, v]) => {
      if (v && typeof v === 'object') {
        nextDrafts[k] = configDrafts[k] ?? JSON.stringify(v, null, 2);
      }
    });
    setConfigDrafts(nextDrafts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.config]);

  // Draft text for object-valued input fields to allow free typing before valid JSON
  const [inputDrafts, setInputDrafts] = useState<Record<number, string>>({});
  useEffect(() => {
    const nextDrafts: Record<number, string> = {};
    inputsArray.forEach((input, index) => {
      if (input.type === 'object' || typeof input.value === 'object') {
        nextDrafts[index] = inputDrafts[index] ?? JSON.stringify(input.value, null, 2);
      }
    });
    setInputDrafts(nextDrafts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.inputs]);

  // Get all available variables from previous steps (both exportable outputs and exportable inputs)
  const getAvailableVariables = (): Array<{stepName: string, outputName: string, reference: string}> => {
    const variables: Array<{stepName: string, outputName: string, reference: string}> = [];
    
    // Separate custom workflow steps from system steps
    const customSteps = workflow.workflow.steps.filter(s => 
      !CONSTANT_STEPS.some(cs => cs.name === s.name)
    );
    const systemSteps = workflow.workflow.steps.filter(s => 
      CONSTANT_STEPS.some(cs => cs.name === s.name)
    );
    
    // Determine if current step is a system step
    const isCurrentStepSystem = CONSTANT_STEPS.some(cs => cs.name === step.name);
    
    if (isCurrentStepSystem) {
      // System steps can reference ALL workflow steps + previous system steps
      const currentSystemIndex = systemSteps.findIndex(s => s.name === step.name);
      const previousSystemSteps = currentSystemIndex >= 0 ? systemSteps.slice(0, currentSystemIndex) : [];
      const availableSteps = [...customSteps, ...previousSystemSteps];
      
      availableSteps.forEach(workflowStep => {
        // Exportable outputs
        if (workflowStep.outputs && workflowStep.outputs.length > 0) {
          workflowStep.outputs.forEach(output => {
            if (output.export) {
              variables.push({
                stepName: workflowStep.name,
                outputName: output.name,
                reference: `\${${workflowStep.name}.${output.name}}`
              });
            }
          });
        }
        // Exportable inputs (array-shaped only)
        if (Array.isArray((workflowStep as any).inputs)) {
          (workflowStep.inputs as any[]).forEach((inp: any) => {
            if (inp?.name && inp?.export) {
              variables.push({
                stepName: workflowStep.name,
                outputName: inp.name,
                reference: `\${${workflowStep.name}.${inp.name}}`
              });
            }
          });
        }
      });
    } else {
      // Workflow steps can reference all other workflow steps (excluding self)
      const availableWorkflowSteps = customSteps.filter(s => s.name !== step.name);
      
      availableWorkflowSteps.forEach(workflowStep => {
        // Exportable outputs
        if (workflowStep.outputs && workflowStep.outputs.length > 0) {
          workflowStep.outputs.forEach(output => {
            if (output.export) {
              variables.push({
                stepName: workflowStep.name,
                outputName: output.name,
                reference: `\${${workflowStep.name}.${output.name}}`
              });
            }
          });
        }
        // Exportable inputs (array-shaped only)
        if (Array.isArray((workflowStep as any).inputs)) {
          (workflowStep.inputs as any[]).forEach((inp: any) => {
            if (inp?.name && inp?.export) {
              variables.push({
                stepName: workflowStep.name,
                outputName: inp.name,
                reference: `\${${workflowStep.name}.${inp.name}}`
              });
            }
          });
        }
      });
    }
    
    return variables;
  };

  const toggleSection = (section: string): void => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const updateStep = (field: keyof WorkflowStep, value: any): void => {
    const updatedStep = {
      ...step,
      [field]: value
    };
    
    // Auto-update 'next' field based on workflow order (for workflow steps only)
    if (field === 'name' || field === 'next') {
      const isSystemStep = CONSTANT_STEPS.some(cs => cs.name === step.name);
      if (!isSystemStep) {
        updatedStep.next = getNextStepName(updatedStep);
      }
    }
    
    onUpdate(updatedStep);
  };

  // Get the next step name based on workflow order
  const getNextStepName = (currentStep: WorkflowStep): string => {
    const customSteps = workflow.workflow.steps.filter(s => 
      !CONSTANT_STEPS.some(cs => cs.name === s.name)
    );
    
    const currentIndex = customSteps.findIndex(s => s.name === currentStep.name);
    
    if (currentIndex === -1) {
      // Step not found, return prepare-authdata as fallback
      return "prepare-authdata";
    }
    
    if (currentIndex < customSteps.length - 1) {
      // Return next workflow step
      return customSteps[currentIndex + 1].name;
    } else {
      // This is the last workflow step, point to first system step
      return "prepare-authdata";
    }
  };


  const addConfigField = (): void => {
    const newConfig = { ...step.config, [`new_config_${Date.now()}`]: '' };
    updateStep('config', newConfig);
  };

  const removeConfigField = (key: string): void => {
    const newConfig = { ...step.config };
    delete newConfig[key];
    updateStep('config', newConfig);
  };

  const updateConfigFieldName = (oldKey: string, newKey: string): void => {
    if (oldKey === newKey) return;
    
    const newConfig = { ...step.config };
    const value = newConfig[oldKey];
    delete newConfig[oldKey];
    newConfig[newKey] = value;
    updateStep('config', newConfig);
  };

  const updateConfigFieldValue = (key: string, value: any): void => {
    const newConfig = { ...step.config, [key]: value };
    let updatedStep = { ...step, config: newConfig };
    
    // If updating function_signature, auto-generate parameters and inputs
    if (key === 'function_signature' && typeof value === 'string' && value.trim()) {
      try {
        const { parameters, inputs } = parseFunctionSignatureWithInputs(value);
        newConfig.parameters = parameters;
        updatedStep.inputs = inputs;
        
        // No global project updates from StepEditor; selection handled in ProjectSetupModal
      } catch (error) {
        console.warn('Failed to parse function signature:', error);
        // Keep the existing parameters or set empty array
        if (!newConfig.parameters) {
          newConfig.parameters = [];
        }
        // Keep existing inputs if parsing fails
      }
    }
    
    onUpdate(updatedStep);
  };

  const addInput = (): void => {
    const newInput: WorkflowInput = {
      name: '',
      type: 'string',
      value: '',
      required: true
    };

    onUpdate({
      ...step,
      inputs: [...inputsArray, newInput]
    });
  };

  const updateInput = (index: number, field: keyof WorkflowInput, value: any): void => {
    const updatedInputs = inputsArray.map((input, i) => 
      i === index ? { ...input, [field]: value } : input
    );

    // Special handling for construct step's value field
    if (isConstruct && field === 'value' && inputsArray[index]?.name === 'value') {
      // Update the construct value object directly
      if (typeof value === 'object' && value !== null) {
        updateConstructValueObj(value);
        return;
      }
    }

    onUpdate({
      ...step,
      inputs: updatedInputs
    });
  };

  const removeInput = (index: number): void => {
    onUpdate({
      ...step,
      inputs: inputsArray.filter((_, i) => i !== index)
    });
  };

  // Support object-shaped inputs (e.g., system steps like prepare-authdata)
  const inputsIsObject = useMemo(
    () => !Array.isArray(step.inputs) && typeof step.inputs === 'object' && step.inputs !== null,
    [step.inputs]
  );
  const inputsObject: Record<string, any> = inputsIsObject ? ((step.inputs as any) || {}) : {};

  const updateInputObjectKey = (oldKey: string, newKey: string) => {
    if (!inputsIsObject || !oldKey) return;
    if (oldKey === newKey) return;
    const obj = { ...inputsObject } as Record<string, any>;
    const val = obj[oldKey];
    delete obj[oldKey];
    obj[newKey] = val;
    onUpdate({ ...step, inputs: obj });
  };

  const updateInputObjectValue = (key: string, value: any) => {
    if (!inputsIsObject) return;
    const obj = { ...inputsObject, [key]: value } as Record<string, any>;
    onUpdate({ ...step, inputs: obj });
  };

  const removeInputObjectKey = (key: string) => {
    if (!inputsIsObject) return;
    const obj = { ...inputsObject } as Record<string, any>;
    delete obj[key];
    onUpdate({ ...step, inputs: obj });
  };

  const addInputObjectKey = () => {
    if (!inputsIsObject) return;
    let base = 'key';
    let k = base;
    let i = 1;
    while (Object.prototype.hasOwnProperty.call(inputsObject, k)) {
      k = `${base}_${i++}`;
    }
    const obj = { ...inputsObject, [k]: '' } as Record<string, any>;
    onUpdate({ ...step, inputs: obj });
  };

  const addOutput = (): void => {
    const newOutput: WorkflowOutput = {
      name: '',
      type: 'string',
      value: '',
      required: true,
      export: true
    };

    onUpdate({
      ...step,
      outputs: [...(step.outputs || []), newOutput]
    });
  };

  const updateOutput = (index: number, field: keyof WorkflowOutput, value: any): void => {
    const updatedOutputs = step.outputs.map((output, i) => 
      i === index ? { ...output, [field]: value } : output
    );

    onUpdate({
      ...step,
      outputs: updatedOutputs
    });
  };

  const removeOutput = (index: number): void => {
    onUpdate({
      ...step,
      outputs: step.outputs.filter((_, i) => i !== index)
    });
  };

  // Helpers to extract API request info from inputs
  const inputsArray: WorkflowInput[] = useMemo(() => {
    return Array.isArray(step.inputs) ? (step.inputs as WorkflowInput[]) : [];
  }, [step.inputs]);

  const inputMap = useMemo(() => {
    const map: Record<string, WorkflowInput> = {};
    inputsArray.forEach((i) => {
      if (i?.name) map[i.name] = i as WorkflowInput;
    });
    return map;
  }, [inputsArray]);

  const resolveInput = <T = any,>(name: string, fallback?: T): T | undefined => {
    const it = inputMap[name];
    return (it?.value as any) ?? fallback;
  };

  // Flatten JSON to dotted paths
  const flattenJson = (obj: any, prefix = ''): Array<{ path: string; value: any }> => {
    const results: Array<{ path: string; value: any }> = [];
    if (obj === null || obj === undefined) {
      return results;
    }
    const isPrimitive = (v: any) => ['string', 'number', 'boolean'].includes(typeof v);
    if (isPrimitive(obj)) {
      results.push({ path: prefix || 'value', value: obj });
      return results;
    }
    if (Array.isArray(obj)) {
      // For arrays, capture as object[] at current prefix and first element deeper
      results.push({ path: prefix || 'list', value: obj });
      obj.forEach((v, idx) => {
        const childPrefix = prefix ? `${prefix}.${idx}` : String(idx);
        results.push(...flattenJson(v, childPrefix));
      });
      return results;
    }
    // Object
    Object.keys(obj).forEach((k) => {
      const childPrefix = prefix ? `${prefix}.${k}` : k;
      const v = (obj as any)[k];
      if (isPrimitive(v)) {
        results.push({ path: childPrefix, value: v });
      } else if (Array.isArray(v)) {
        results.push({ path: childPrefix, value: v });
        v.forEach((vv, idx) => {
          results.push(...flattenJson(vv, `${childPrefix}.${idx}`));
        });
      } else if (v && typeof v === 'object') {
        results.push({ path: childPrefix, value: v });
        results.push(...flattenJson(v, childPrefix));
      }
    });
    return results;
  };

  const inferType = (v: any): WorkflowOutput['type'] => {
    const t = typeof v;
    if (t === 'string') return 'string';
    if (t === 'number') return 'number';
    if (t === 'boolean') return 'boolean';
    if (Array.isArray(v)) return 'object[]';
    if (v && typeof v === 'object') return 'object';
    return 'string';
  };

  const autoFillOutputsFromApi = async () => {
    setAutoMapError(null);
    setAutoMapLoading(true);
    try {
      let url = String(resolveInput('url', ''));
      let method = String(resolveInput('method', 'GET'));
      let headers = resolveInput<Record<string, string>>('headers', {} as any) || {};
      let body = resolveInput<any>('body', undefined);
      let timeoutSec = Number(resolveInput('timeout', 30));

      // If inputs contain variable placeholders, execute prior steps and resolve them
      const hasVars = (v: any) => typeof v === 'string' && /\$\{[^}]+\}/.test(v);
      const anyVars = hasVars(url) || hasVars(method) || hasVars(body) || Object.values(headers || {}).some(hasVars);
      if (anyVars) {
        const executor = new WorkflowExecutor();
        const ctx = await executor.executeUntil(workflow, step.name);
        const resolved = executor.resolveInputsWithVariables({ url, method, headers, body, timeoutSec }, ctx.variables);
        url = String(resolved.url || url);
        method = String(resolved.method || method);
        headers = (resolved.headers || headers) as Record<string, string>;
        body = resolved.body ?? body;
        timeoutSec = Number(resolved.timeoutSec ?? timeoutSec);
      }

      if (!url) {
        throw new Error('Missing "url" input');
      }

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), Math.max(1, timeoutSec) * 1000);

      // Decide whether to use proxy
      let isExternal = false;
      try {
        const u = new URL(url, window.location.origin);
        isExternal = !!u.origin && u.origin !== window.location.origin && /^https?:$/.test(u.protocol);
      } catch {
        isExternal = false;
      }

      let resp: Response;
      if (isExternal) {
        // Use serverless proxy to bypass CORS and ngrok interstitials
        resp = await fetch('/api/proxy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url, method, headers, body }),
          signal: controller.signal,
        } as RequestInit);
      } else {
        // Inject ngrok header to skip free plan browser warning if needed
        const finalHeaders: Record<string, string> = { ...(headers as any) };
        if (/ngrok(-free)?\.app|ngrok/i.test(url)) {
          finalHeaders['ngrok-skip-browser-warning'] = 'true';
        }

        resp = await fetch(url, {
          method,
          headers: finalHeaders as any,
          body: method.toUpperCase() === 'GET' || method.toUpperCase() === 'HEAD' ? undefined : (typeof body === 'object' ? JSON.stringify(body) : body),
          signal: controller.signal,
        } as RequestInit);
      }

      clearTimeout(timer);

      const contentType = resp.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      const payload = isJson ? await resp.json().catch(() => ({})) : await resp.text();

      const outputsFromResponse: WorkflowOutput[] = [];
      if (isJson) {
        const flattened = flattenJson(payload);
        // Deduplicate by path
        const seen = new Set<string>();
        flattened.forEach(({ path, value }) => {
          if (seen.has(path)) return;
          seen.add(path);
          outputsFromResponse.push({
            name: path,
            type: inferType(value),
            value: `response.body.${path}`,
            required: true,
            export: true,
          });
        });
      } else {
        // Non-JSON: provide a single output
        outputsFromResponse.push({
          name: 'text',
          type: 'string',
          value: 'response.body',
          required: true,
          export: true,
        });
      }

      onUpdate({
        ...step,
        outputs: outputsFromResponse,
      });
    } catch (e: any) {
      setAutoMapError(e?.message || 'Failed to fetch API');
    } finally {
      setAutoMapLoading(false);
    }
  };

  const isConstruct = step.name === 'construct-evm';
  const constructInputsAny = isConstruct ? (step as any).inputs : null;
  let constructValueObj: Record<string, any> = {};
  if (isConstruct && constructInputsAny) {
    if (Array.isArray(constructInputsAny)) {
      const valueItem = (constructInputsAny as any[]).find((i: any) => i?.name === 'value');
      if (valueItem && typeof valueItem.value === 'object' && valueItem.value !== null) {
        constructValueObj = valueItem.value as Record<string, any>;
      }
    } else if (typeof constructInputsAny === 'object') {
      constructValueObj = (constructInputsAny as any).value || {};
    }
  }
  // Construct: we no longer auto-select outputs; inputs will be built from config.parameters

  // Helpers for construct-evm Config (parameters editing)
  const constructParams: Array<{ name: string; type: string; [k: string]: any }>
    = Array.isArray((step as any).config?.parameters) ? (step as any).config?.parameters : [];
  const projectName = workflow?.workflow?.name || '';
  const camelCase = (s: string) => s
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w, i) => i === 0 ? w.toLowerCase() : (w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join('');
  const defaultParamName = camelCase(projectName) || 'data';

  // Ensure a single parameter for construct-evm (defaults to tuple but can be changed)
  useEffect(() => {
    if (!isConstruct) return;
    const params = constructParams;
    if (!Array.isArray(params) || params.length === 0) {
      // Create default single tuple parameter if none exist
      const next = [{ name: defaultParamName, type: 'tuple', components: [] }];
      const newConfig = { ...(step as any).config, parameters: next };
      onUpdate({ ...(step as any), config: newConfig });
      return;
    }
    // If multiple parameters exist, keep only the first one
    if (params.length > 1) {
      const next = [params[0]];
      const newConfig = { ...(step as any).config, parameters: next };
      onUpdate({ ...(step as any), config: newConfig });
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConstruct, JSON.stringify(constructParams), defaultParamName]);

  const updateConstructParam = (index: number, field: string, value: any) => {
    if (!isConstruct) return;
    const next = [...constructParams];
    const prev = { ...next[index] } as any;
    next[index] = { ...next[index], [field]: value };
    const newConfig = { ...(step as any).config, parameters: next };
    // If renaming the tuple param, also rename the key in inputs.value
    if (field === 'name' && index === 0 && prev?.name && prev.name !== value) {
      const current = { ...(constructValueObj || {}) } as Record<string, any>;
      if (current && Object.prototype.hasOwnProperty.call(current, prev.name)) {
        const moved = current[prev.name];
        delete current[prev.name];
        current[String(value || '') || defaultParamName] = moved;
        updateConstructValueObj(current);
      }
    }
    onUpdate({ ...(step as any), config: newConfig });
  };

  const addConstructParam = () => {
    // No-op: only one tuple parameter allowed
    return;
  };

  const removeConstructParam = (_index: number) => {
    // No-op: cannot remove the single required tuple parameter
    return;
  };

  const addConstructParamComponent = (paramIndex: number) => {
    if (!isConstruct) return;
    const next = [...constructParams];
    const param = { ...next[paramIndex] } as any;
    const comps = Array.isArray(param.components) ? [...param.components] : [];
    comps.push({ name: '', type: '' });
    param.components = comps;
    next[paramIndex] = param;
    const newConfig = { ...(step as any).config, parameters: next };
    onUpdate({ ...(step as any), config: newConfig });
  };

  const updateConstructParamComponent = (paramIndex: number, compIndex: number, field: string, value: any) => {
    if (!isConstruct) return;
    const next = [...constructParams];
    const param = { ...next[paramIndex] } as any;
    const comps = Array.isArray(param.components) ? [...param.components] : [];
    comps[compIndex] = { ...comps[compIndex], [field]: value };
    param.components = comps;
    next[paramIndex] = param;
    const newConfig = { ...(step as any).config, parameters: next };
    onUpdate({ ...(step as any), config: newConfig });
  };

  const removeConstructParamComponent = (paramIndex: number, compIndex: number) => {
    if (!isConstruct) return;
    const next = [...constructParams];
    const param = { ...next[paramIndex] } as any;
    const comps = (Array.isArray(param.components) ? param.components : []).filter((_: any, i: number) => i !== compIndex);
    param.components = comps;
    next[paramIndex] = param;
    const newConfig = { ...(step as any).config, parameters: next };
    onUpdate({ ...(step as any), config: newConfig });
  };

  const updateConstructValueObj = (next: Record<string, any>) => {
    if (!isConstruct) return;
    const updated: any = { ...step };
    // Deep-sort object keys alphabetically for stable order
    const deepSort = (obj: any): any => {
      if (Array.isArray(obj) || obj === null || typeof obj !== 'object') return obj;
      const sortedKeys = Object.keys(obj).sort((a, b) => a.localeCompare(b));
      const out: Record<string, any> = {};
      sortedKeys.forEach(k => {
        out[k] = deepSort(obj[k]);
      });
      return out;
    };
    const sortedNext = deepSort(next);
    // Write back as inputs array so the standard Inputs section renders
    updated.inputs = [
      {
        name: 'value',
        type: 'json',
        value: sortedNext,
        required: true,
        description: 'Auto-generated Construct inputs'
      }
    ] as any;
    onUpdate(updated as WorkflowStep);
  };

  const removeConstructKey = (k: string) => {
    if (!isConstruct) return;
    const next = { ...(constructValueObj || {}) } as Record<string, any>;
    delete next[k];
    updateConstructValueObj(next);
  };

  // Track previous config parameters to detect actual changes
  const prevConfigParams = React.useRef<string>('');

  // Auto-construct inputs from config.parameters (alphabetical), preserving existing values where possible
  useEffect(() => {
    if (!isConstruct) return;
    const params: any[] = Array.isArray((step as any).config?.parameters) ? (step as any).config?.parameters : [];
    const currentParamsStr = JSON.stringify(params);
    
    // Skip if parameters haven't actually changed (prevents re-running on editor reopen)
    if (prevConfigParams.current === currentParamsStr) return;
    
    // Update tracker immediately to prevent loops
    prevConfigParams.current = currentParamsStr;
    
    // Deep sort function to alphabetically order all object keys
    const deepSort = (obj: any): any => {
      if (Array.isArray(obj) || obj === null || typeof obj !== 'object') return obj;
      const sortedKeys = Object.keys(obj).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      const out: Record<string, any> = {};
      sortedKeys.forEach(k => out[k] = deepSort(obj[k]));
      return out;
    };
    
    // Build inputs structure from parameters, sorting alphabetically and preserving ALL existing values
    const buildFromParams = (ps: any[], existing: any): any => {
      const result: Record<string, any> = {};
      
      // Sort parameters alphabetically by name first
      const sortedParams = [...ps].sort((a, b) => 
        (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' })
      );
      
      sortedParams.forEach(p => {
        const name = p?.name || '';
        if (!name) return;
        
        if (p?.type === 'tuple' && Array.isArray(p?.components)) {
          // For tuple types: create nested object with component fields
          const prev = (existing && typeof existing === 'object') ? existing[name] : undefined;
          const obj: Record<string, any> = {};
          
          // Sort tuple components alphabetically
          const sortedComponents = [...p.components].sort((a: any, b: any) => 
            (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' })
          );
          
          sortedComponents.forEach((c: any) => {
            const cName = c?.name || '';
            if (!cName) return;
            // PRESERVE existing values - only use empty string if truly undefined
            obj[cName] = (prev && typeof prev === 'object' && prev[cName] !== undefined) ? prev[cName] : '';
          });
          result[name] = obj;
        } else {
          // For non-tuple types: use simple string value, not object
          // PRESERVE existing value if it's a string/primitive, otherwise use empty string
          const prev = (existing && typeof existing === 'object') ? existing[name] : undefined;
          result[name] = (typeof prev === 'string' || typeof prev === 'number' || typeof prev === 'boolean') 
            ? prev 
            : '';
        }
      });
      
      return deepSort(result);
    };
    
    const nextValue = buildFromParams(params, constructValueObj);
    
    // Always update when parameters change (we already checked they changed above)
    // This ensures removals, additions, and renames all trigger input updates
    updateConstructValueObj(nextValue);
    
    // Also update the draft to reflect the sorted structure
    const valueIndex = inputsArray.findIndex(inp => inp.name === 'value');
    if (valueIndex >= 0) {
      setInputDrafts(prev => ({ ...prev, [valueIndex]: JSON.stringify(nextValue, null, 2) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConstruct, JSON.stringify((step as any).config?.parameters || [])]);

  // removed old preview/selection helpers

  return (
    <div className="space-y-3">
      {/* Basic Step Info */}
      {!isConstant && (
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-2">Step Name</label>
            <input
              type="text"
              value={step.name}
              onChange={(e) => updateStep('name', e.target.value)}
              className={cn(
                "w-full p-2 text-sm bg-n8n-dark-800 border border-n8n-dark-600 rounded-n8n",
                "text-white placeholder-n8n-dark-400 focus:ring-2 focus:ring-n8n-primary-500",
                "focus:border-n8n-primary-500 transition-all duration-200"
              )}
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-2">Type</label>
            <input
              type="number"
              value={step.type || ''}
              onChange={(e) => updateStep('type', e.target.value ? Number(e.target.value) : undefined)}
              className={cn(
                "w-full p-2 text-sm bg-n8n-dark-800 border border-n8n-dark-600 rounded-n8n",
                "text-white placeholder-n8n-dark-400 focus:ring-2 focus:ring-n8n-primary-500",
                "focus:border-n8n-primary-500 transition-all duration-200"
              )}
              placeholder="e.g., 10"
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-2">
              Next Step {!CONSTANT_STEPS.some(cs => cs.name === step.name) && <span className="text-n8n-primary-400">(Auto-calculated)</span>}
            </label>
            <input
              type="text"
              value={CONSTANT_STEPS.some(cs => cs.name === step.name) ? (step.next || '') : getNextStepName(step)}
              onChange={(e) => updateStep('next', e.target.value)}
              readOnly={!CONSTANT_STEPS.some(cs => cs.name === step.name)}
              className={cn(
                "w-full p-2 text-sm bg-n8n-dark-800 border border-n8n-dark-600 rounded-n8n",
                "text-white placeholder-n8n-dark-400 focus:ring-2 focus:ring-n8n-primary-500",
                "focus:border-n8n-primary-500 transition-all duration-200"
              )}
            />
          </div>
        </div>
      )}

      {/* Configuration */}
      <div className="bg-n8n-dark-800 rounded-n8n border border-n8n-dark-600 shadow-n8n">
        <button
          onClick={() => toggleSection('config')}
          className={cn(
            "w-full px-3 py-2 text-left flex items-center justify-between",
            "hover:bg-n8n-dark-700 transition-colors duration-200 rounded-t-n8n",
            !expandedSections.config && "rounded-b-n8n"
          )}
        >
          <div className="flex items-center">
            <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
            <span className="text-sm font-medium text-white">Configuration</span>
          </div>
          {expandedSections.config ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </button>
        {expandedSections.config && (
          <div className="px-3 pb-3 border-t border-n8n-dark-700">
            {!isConstruct ? (
              <div className="space-y-3 mt-3">
                {Object.entries(step.config || {}).map(([key, value], index) => (
                  <div key={`config-${index}`} className="flex items-start space-x-2">
                    <div className="flex-1">
                      <input
                        type="text"
                        defaultValue={key}
                        onBlur={(e) => updateConfigFieldName(key, e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                        className={cn(
                          "w-full p-1 text-xs font-medium bg-gray-700 border border-gray-500 rounded mb-1",
                          "text-gray-300 focus:ring-1 focus:ring-blue-500 focus:border-blue-500",
                          "transition-all duration-200"
                        )}
                        placeholder="Field name"
                      />
                      {typeof value === 'object' && value !== null ? (
                        <textarea
                          value={configDrafts[key] ?? JSON.stringify(value, null, 2)}
                          onChange={(e) => setConfigDrafts((prev) => ({ ...prev, [key]: e.target.value }))}
                          onBlur={(e) => {
                            const text = e.target.value;
                            try { updateConfigFieldValue(key, JSON.parse(text)); } catch {}
                          }}
                          className={cn(
                            "w-full p-2 text-xs font-mono bg-gray-800 border border-gray-600 rounded-lg resize-none",
                            "text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500",
                            "focus:border-blue-500 transition-all duration-200"
                          )}
                          rows={4}
                          placeholder={`Enter ${key.replace(/_/g, ' ')} JSON`}
                        />
                      ) : (
                        <input
                          type={typeof value === 'number' ? 'number' : 'text'}
                          value={typeof value === 'boolean' ? String(value) : String(value)}
                          onChange={(e) => {
                            let newValue: any = e.target.value;
                            if (typeof value === 'boolean') newValue = e.target.value === 'true';
                            else if (typeof value === 'number') newValue = Number(e.target.value) || 0;
                            updateConfigFieldValue(key, newValue);
                          }}
                          className={cn(
                            "w-full p-2 text-sm bg-gray-800 border border-gray-600 rounded-lg",
                            "text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500",
                            "focus:border-blue-500 transition-all duration-200"
                          )}
                          placeholder={`Enter ${key.replace(/_/g, ' ')} or use \${step-name.output-name}`}
                        />
                      )}
                    </div>
                    <button onClick={() => removeConfigField(key)} className="mt-5 p-1 text-red-500 hover:text-red-700 transition-colors duration-200">
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={addConfigField}
                  className={cn(
                    "w-full p-2 border-2 border-dashed border-gray-600 rounded-lg",
                    "text-gray-400 hover:border-blue-500 hover:text-blue-400",
                    "transition-all duration-200 flex items-center justify-center space-x-1"
                  )}
                >
                  <Plus size={14} />
                  <span className="text-sm">Add Config Field</span>
                </button>
              </div>
            ) : (
              <div className="space-y-3 mt-3">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-medium text-gray-300">Parameter (Single)</label>
                  </div>
                  <div className="space-y-3">
                    {constructParams.map((p, idx) => (
                      <div key={`param-${idx}`} className="space-y-2 p-2 rounded-lg border border-gray-700 bg-gray-800/40">
                        <div className="grid grid-cols-2 gap-2 items-end">
                          <div>
                            <label className="block text-[10px] text-gray-400 mb-1">Name</label>
                            <input
                              type="text"
                              value={p.name}
                              onChange={(e) => updateConstructParam(idx, 'name', e.target.value)}
                              className={cn("w-full p-2 text-sm bg-gray-800 border border-gray-600 rounded-lg text-white")}
                              placeholder="paramName"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-gray-400 mb-1">Type</label>
                            <select
                              value={p.type}
                              onChange={(e) => updateConstructParam(idx, 'type', e.target.value)}
                              className={cn("w-full p-2 text-sm bg-gray-800 border border-gray-600 rounded-lg text-white")}
                            >
                              <option value="tuple">tuple</option>
                              <option value="uint256">uint256</option>
                              <option value="uint8">uint8</option>
                              <option value="uint16">uint16</option>
                              <option value="uint32">uint32</option>
                              <option value="uint64">uint64</option>
                              <option value="uint128">uint128</option>
                              <option value="int256">int256</option>
                              <option value="address">address</option>
                              <option value="bool">bool</option>
                              <option value="bytes">bytes</option>
                              <option value="bytes32">bytes32</option>
                              <option value="string">string</option>
                            </select>
                          </div>
                        </div>
                        {String(p.type).trim().toLowerCase() === 'tuple' && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[11px] text-gray-300">Fields</span>
                              <button onClick={() => addConstructParamComponent(idx)} className="text-[11px] px-2 py-0.5 rounded bg-gray-700 hover:bg-gray-600 border border-gray-500">Add Field</button>
                            </div>
                            <div className="space-y-2">
                              {(Array.isArray((p as any).components) ? (p as any).components : []).map((c: any, cIdx: number) => (
                                <div key={`param-${idx}-comp-${cIdx}`} className="grid grid-cols-5 gap-2 items-end">
                                  <div className="col-span-2">
                                    <label className="block text-[10px] text-gray-400 mb-1">Name</label>
                                    <input
                                      type="text"
                                      value={c.name || ''}
                                      onChange={(e) => updateConstructParamComponent(idx, cIdx, 'name', e.target.value)}
                                      className={cn("w-full p-2 text-sm bg-gray-800 border border-gray-600 rounded-lg text-white")}
                                      placeholder="componentName"
                                    />
                                  </div>
                                  <div className="col-span-2">
                                    <label className="block text-[10px] text-gray-400 mb-1">Type</label>
                                    <select
                                      value={c.type || ''}
                                      onChange={(e) => updateConstructParamComponent(idx, cIdx, 'type', e.target.value)}
                                      className={cn("w-full p-2 text-sm bg-gray-800 border border-gray-600 rounded-lg text-white")}
                                    >
                                      <option value="">Select type...</option>
                                      <option value="uint256">uint256</option>
                                      <option value="uint8">uint8</option>
                                      <option value="uint16">uint16</option>
                                      <option value="uint32">uint32</option>
                                      <option value="uint64">uint64</option>
                                      <option value="uint128">uint128</option>
                                      <option value="int256">int256</option>
                                      <option value="address">address</option>
                                      <option value="bool">bool</option>
                                      <option value="bytes">bytes</option>
                                      <option value="bytes32">bytes32</option>
                                      <option value="string">string</option>
                                    </select>
                                  </div>
                                  <div className="text-right">
                                    <button onClick={() => removeConstructParamComponent(idx, cIdx)} className="p-1 text-red-500 hover:text-red-700">
                                      <X size={14} />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-n8n-dark-300">Construct inputs are auto-generated from Parameters and sorted alphabetically.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Inputs */}
      <div className="bg-n8n-dark-800 rounded-n8n border border-n8n-dark-600 shadow-n8n">
        <button
          onClick={() => toggleSection('inputs')}
          className={cn(
            "w-full px-3 py-2 text-left flex items-center justify-between",
            "hover:bg-n8n-dark-700 transition-colors duration-200 rounded-t-n8n",
            !expandedSections.inputs && "rounded-b-lg"
          )}
        >
          <div className="flex items-center">
            <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
            <span className="text-sm font-medium text-white">Inputs</span>
          </div>
          {expandedSections.inputs ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </button>
        {expandedSections.inputs && (
          <div className="px-3 pb-3 border-t border-n8n-dark-700">
            {/* Variable Reference Helper */}
            {getAvailableVariables().length > 0 && (
              <div className="mt-3 mb-4 p-3 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n">
                <div className="flex items-center space-x-2 mb-2">
                  <Hash size={14} className="text-n8n-primary-400" />
                  <span className="text-xs font-medium text-n8n-dark-200">Available Variables</span>
                </div>
                <div className="grid grid-cols-1 gap-1 max-h-32 overflow-y-auto">
                  {getAvailableVariables().map((variable, index) => (
                    <div
                      key={index}
                      className="text-xs text-n8n-dark-300 hover:text-n8n-primary-300 cursor-pointer font-mono bg-n8n-dark-800 px-2 py-1 rounded border border-n8n-dark-600 hover:border-n8n-primary-500 transition-colors"
                      onClick={() => {
                        navigator.clipboard.writeText(variable.reference);
                      }}
                      title={`Click to copy: ${variable.reference}`}
                    >
                      <span className="text-n8n-primary-400">{variable.stepName}</span>
                      <span className="text-n8n-dark-400">.</span>
                      <span className="text-n8n-success-400">{variable.outputName}</span>
                    </div>
                  ))}
                </div>
                <div className="text-xs text-n8n-dark-400 mt-2">
                  💡 Click any variable to copy its reference syntax
                </div>
              </div>
            )}
            
            <div className="space-y-3 mt-3">
              {inputsIsObject && (
                <>
                  {Object.entries(inputsObject).map(([key, val], idx) => (
                    <div key={`in-obj-${idx}`} className="flex items-start space-x-2">
                      <div className="flex-1 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-300 mb-1">Name</label>
                            <input
                              type="text"
                              defaultValue={key}
                              onBlur={(e) => updateInputObjectKey(key, e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur(); }}
                              className={cn(
                                "w-full p-2 text-sm bg-gray-800 border border-gray-600 rounded-lg",
                                "text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500",
                                "focus:border-blue-500 transition-all duration-200"
                              )}
                              placeholder="e.g. result"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-300 mb-1">Type</label>
                            <input
                              type="text"
                              value={typeof val}
                              readOnly
                              className={cn(
                                "w-full p-2 text-sm bg-gray-900 border border-gray-700 rounded-lg",
                                "text-gray-400"
                              )}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">Value</label>
                          {typeof val === 'object' ? (
                            <textarea
                              value={JSON.stringify(val, null, 2)}
                              onChange={(e) => {
                                try {
                                  const parsed = JSON.parse(e.target.value);
                                  updateInputObjectValue(key, parsed);
                                } catch {
                                  // ignore invalid JSON while typing
                                }
                              }}
                              className={cn(
                                "w-full p-2 text-xs font-mono bg-gray-800 border border-gray-600 rounded-lg resize-none",
                                "text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500",
                                "focus:border-blue-500 transition-all duration-200"
                              )}
                              rows={6}
                              placeholder="JSON object"
                            />
                          ) : (
                            <input
                              type="text"
                              value={String(val)}
                              onChange={(e) => updateInputObjectValue(key, e.target.value)}
                              className={cn(
                                "w-full p-2 text-sm bg-gray-800 border border-gray-600 rounded-lg",
                                "text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500",
                                "focus:border-blue-500 transition-all duration-200"
                              )}
                              placeholder={`Enter value or use \${step-name.output-name}`}
                            />
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => removeInputObjectKey(key)}
                        className="mt-7 p-1 text-red-500 hover:text-red-700 transition-colors duration-200"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={addInputObjectKey}
                    className={cn(
                      "w-full p-2 border-2 border-dashed border-gray-600 rounded-lg",
                      "text-gray-400 hover:border-blue-500 hover:text-blue-400",
                      "transition-all duration-200 flex items-center justify-center space-x-1"
                    )}
                  >
                    <Plus size={14} />
                    <span className="text-sm">Add Input</span>
                  </button>
                </>
              )}
              {!inputsIsObject && inputsArray.map((input, index) => (
                <React.Fragment key={index}>
                  <div className="flex items-start space-x-2">
                    <div className="flex-1 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">Name</label>
                          <input
                            type="text"
                            value={input.name}
                            onChange={(e) => updateInput(index, 'name', e.target.value)}
                            readOnly={isConstruct}
                            className={cn(
                              "w-full p-2 text-sm border rounded-lg",
                              isConstruct 
                                ? "bg-gray-900 border-gray-700 text-gray-400 cursor-not-allowed"
                                : "bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                              "transition-all duration-200"
                            )}
                            placeholder="e.g. authData, amount"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">Type</label>
                          {isConstruct ? (
                            <input
                              type="text"
                              value={input.type || 'json'}
                              readOnly
                              className={cn(
                                "w-full p-2 text-sm bg-gray-900 border border-gray-700 rounded-lg",
                                "text-gray-400 cursor-not-allowed"
                              )}
                            />
                          ) : (
                            <select
                              value={input.type || 'string'}
                              onChange={(e) => updateInput(index, 'type', e.target.value)}
                              className={cn(
                                "w-full p-2 text-sm bg-gray-800 border border-gray-600 rounded-lg",
                                "text-white focus:ring-2 focus:ring-blue-500",
                                "focus:border-blue-500 transition-all duration-200"
                              )}
                            >
                              <option value="string">String</option>
                              <option value="uint256">Uint256</option>
                              <option value="address">Address</option>
                              <option value="bytes32">Bytes32</option>
                              <option value="bytes">Bytes</option>
                              <option value="bool">Bool</option>
                              <option value="object">Object</option>
                              <option value="object[]">Object Array</option>
                            </select>
                          )}
                        </div>
                      </div>
                      {isConstruct && (
                        <div className="text-xs text-n8n-dark-400 mb-2">
                          💡 Name and Type are fixed for EVM Construct. Edit the Value field to map workflow outputs.
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-gray-300 mb-1">Value</label>
                        {input.type === 'object' || typeof input.value === 'object' ? (
                          <textarea
                            value={inputDrafts[index] ?? JSON.stringify(input.value, null, 2)}
                            onChange={(e) => {
                              const newText = e.target.value;
                              setInputDrafts(prev => ({ ...prev, [index]: newText }));
                              try {
                                const parsed = JSON.parse(newText);
                                // Auto-format with proper spacing
                                const formatted = JSON.stringify(parsed, null, 2);
                                setInputDrafts(prev => ({ ...prev, [index]: formatted }));
                                updateInput(index, 'value', parsed);
                              } catch (error) {
                                // Try to fix JS object notation (unquoted keys) to JSON
                                try {
                                  const fixed = newText
                                    .replace(/(\w+):/g, '"$1":') // Add quotes around keys
                                    .replace(/,\s*}/g, '}')      // Remove trailing commas
                                    .replace(/,\s*]/g, ']');     // Remove trailing commas in arrays
                                  const parsed = JSON.parse(fixed);
                                  const formatted = JSON.stringify(parsed, null, 2);
                                  setInputDrafts(prev => ({ ...prev, [index]: formatted }));
                                  updateInput(index, 'value', parsed);
                                } catch (e) {
                                  // Still invalid, keep draft but don't update actual value
                                }
                              }
                            }}
                            onBlur={(e) => {
                              // On blur, try to fix and format the JSON
                              const text = e.target.value;
                              try {
                                const parsed = JSON.parse(text);
                                const formatted = JSON.stringify(parsed, null, 2);
                                setInputDrafts(prev => ({ ...prev, [index]: formatted }));
                                updateInput(index, 'value', parsed);
                              } catch (error) {
                                // Try to fix JS object notation to JSON
                                try {
                                  const fixed = text
                                    .replace(/(\w+):/g, '"$1":')
                                    .replace(/,\s*}/g, '}')
                                    .replace(/,\s*]/g, ']');
                                  const parsed = JSON.parse(fixed);
                                  const formatted = JSON.stringify(parsed, null, 2);
                                  setInputDrafts(prev => ({ ...prev, [index]: formatted }));
                                  updateInput(index, 'value', parsed);
                                } catch (e) {
                                  // If still invalid, keep the draft as-is
                                }
                              }
                            }}
                            className={cn(
                              "w-full p-2 text-xs font-mono bg-gray-800 border border-gray-600 rounded-lg resize-none",
                              "text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500",
                              "focus:border-blue-500 transition-all duration-200"
                            )}
                            rows={6}
                            placeholder="JSON object"
                          />
                        ) : (
                          <input
                            type={input.type === 'uint256' ? 'text' : 'text'}
                            value={typeof input.value === 'boolean' ? String(input.value) : String(input.value)}
                            onChange={(e) => {
                              let newValue: any = e.target.value;
                              if (input.type === 'bool') {
                                newValue = e.target.value === 'true';
                              }
                              updateInput(index, 'value', newValue);
                            }}
                            className={cn(
                              "w-full p-2 text-sm bg-gray-800 border border-gray-600 rounded-lg",
                              "text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500",
                              "focus:border-blue-500 transition-all duration-200"
                            )}
                            placeholder={`Enter value or use \${step-name.output-name}`}
                          />
                        )}
                      </div>
                      {/* Required and Description fields removed per request */}
                    </div>
                    {!isConstruct && (
                      <button
                        onClick={() => removeInput(index)}
                        className="mt-7 p-1 text-red-500 hover:text-red-700 transition-colors duration-200"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  {index < inputsArray.length - 1 && (
                    <div className="my-4 mb-6 flex items-center select-none">
                      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-600 to-transparent" />
                      <span className="mx-3 text-[10px] tracking-wider uppercase text-gray-400/80">
                        {index + 1}
                      </span>
                      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-600 to-transparent" />
                    </div>
                  )}
                </React.Fragment>
              ))}
              {!inputsIsObject && !isConstruct && (
                <button
                  onClick={addInput}
                  className={cn(
                    "w-full p-2 border-2 border-dashed border-gray-600 rounded-lg",
                    "text-gray-400 hover:border-blue-500 hover:text-blue-400",
                    "transition-all duration-200 flex items-center justify-center space-x-1"
                  )}
                >
                  <Plus size={14} />
                  <span className="text-sm">Add Input</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Outputs Section */}
        <button
          onClick={() => toggleSection('outputs')}
          className={cn(
            "w-full px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all duration-200",
            "flex items-center justify-between text-left border border-gray-600",
            expandedSections.outputs && "bg-gray-700"
          )}
        >
          <div className="flex items-center">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
            <span className="text-sm font-medium text-white">Outputs</span>
          </div>
          {expandedSections.outputs ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </button>
        {expandedSections.outputs && (
          <div className="px-3 pb-3 border-t border-n8n-dark-700">
            {/* Output Naming Helper */}
            <div className="mt-3 mb-4 p-3 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n">
              <div className="flex items-center space-x-2 mb-2">
                <Hash size={14} className="text-n8n-success-400" />
                <span className="text-xs font-medium text-n8n-dark-200">Output Naming</span>
              </div>
              <div className="text-xs text-n8n-dark-300 space-y-1">
                <div>✅ Use dotted notation: <code className="text-n8n-success-400 bg-n8n-dark-800 px-1 rounded">address.street</code>, <code className="text-n8n-success-400 bg-n8n-dark-800 px-1 rounded">zestimate.amount.value</code></div>
                <div>✅ This enables references like: <code className="text-n8n-primary-400 bg-n8n-dark-800 px-1 rounded">${`{${step.name}.address.street}`}</code></div>
              </div>
            </div>

            {/* Auto map from API response */}
            {!isConstant && (
              <div className="mb-3">
                <button
                  onClick={autoFillOutputsFromApi}
                  disabled={autoMapLoading}
                  className={cn(
                    "px-3 py-2 text-xs rounded bg-n8n-primary-600 hover:bg-n8n-primary-500",
                    "text-white border border-n8n-primary-500 disabled:opacity-60"
                  )}
                >
                  {autoMapLoading ? 'Fetching…' : 'Auto-fill from API response'}
                </button>
                {autoMapError && (
                  <div className="mt-2 text-xs text-red-400">{autoMapError}</div>
                )}
                <div className="mt-1 text-[11px] text-n8n-dark-300">
                  Uses Inputs named <code>url</code>, <code>method</code>, <code>headers</code>, <code>body</code>, <code>timeout</code> if present.
                </div>
              </div>
            )}
            
            <div className="space-y-3 mt-3">
              {(step.outputs || []).map((output, index) => (
                <React.Fragment key={index}>
                  <div className="flex items-start space-x-2">
                    <div className="flex-1 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">Name</label>
                          <input
                            type="text"
                            value={output.name}
                            onChange={(e) => updateOutput(index, 'name', e.target.value)}
                            className={cn(
                              "w-full p-2 text-sm bg-gray-800 border border-gray-600 rounded-lg",
                              "text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500",
                              "focus:border-blue-500 transition-all duration-200"
                            )}
                            placeholder="e.g. address.street, zestimate.amount.value"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">Type</label>
                          <select
                            value={output.type || 'string'}
                            onChange={(e) => updateOutput(index, 'type', e.target.value)}
                            className={cn(
                              "w-full p-2 text-sm bg-gray-800 border border-gray-600 rounded-lg",
                              "text-white focus:ring-2 focus:ring-blue-500",
                              "focus:border-blue-500 transition-all duration-200"
                            )}
                          >
                            <option value="string">String</option>
                            <option value="number">Number</option>
                            <option value="boolean">Boolean</option>
                            <option value="bool">Bool</option>
                            <option value="object">Object</option>
                            <option value="object[]">Object Array</option>
                            <option value="json">JSON</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-300 mb-1">Value</label>
                        <input
                          type="text"
                          value={output.value}
                          onChange={(e) => updateOutput(index, 'value', e.target.value)}
                          className={cn(
                            "w-full p-2 text-sm bg-gray-800 border border-gray-600 rounded-lg",
                            "text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500",
                            "focus:border-blue-500 transition-all duration-200"
                          )}
                          placeholder="e.g., response.body.result or result"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">Required</label>
                          <input
                            type="text"
                            value={output.required.toString()}
                            onChange={(e) => updateOutput(index, 'required', e.target.value === 'true')}
                            className={cn(
                              "w-full p-2 text-sm bg-gray-800 border border-gray-600 rounded-lg",
                              "text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500",
                              "focus:border-blue-500 transition-all duration-200"
                            )}
                            placeholder="true or false"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">Export</label>
                          <input
                            type="text"
                            value={output.export.toString()}
                            onChange={(e) => updateOutput(index, 'export', e.target.value === 'true')}
                            className={cn(
                              "w-full p-2 text-sm bg-gray-800 border border-gray-600 rounded-lg",
                              "text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500",
                              "focus:border-blue-500 transition-all duration-200"
                            )}
                            placeholder="true or false"
                          />
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeOutput(index)}
                      className="mt-7 p-1 text-red-500 hover:text-red-700 transition-colors duration-200"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {index < step.outputs?.length - 1 && (
                    <div className="my-4 mb-6 flex items-center select-none">
                      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-600 to-transparent" />
                      <span className="mx-3 text-[10px] tracking-wider uppercase text-gray-400/80">
                        {index + 1}
                      </span>
                      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-600 to-transparent" />
                    </div>
                  )}
                </React.Fragment>
              ))}
              <button
                onClick={addOutput}
                className={cn(
                  "w-full p-2 border-2 border-dashed border-gray-600 rounded-lg",
                  "text-gray-400 hover:border-blue-500 hover:text-blue-400",
                  "transition-all duration-200 flex items-center justify-center space-x-1"
                )}
              >
                <Plus size={14} />
                <span className="text-sm">Add Output</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StepEditor;